import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { EyebrowBadge } from "@/components/EyebrowBadge";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import {
  getMySubscription,
  createPortalSession,
  cancelPremium,
  resumePremium,
} from "@/lib/subscriptions.functions";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getStripeEnvironment } from "@/lib/stripe";
import { Check, Loader2, ExternalLink, Crown, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLocale } from "@/lib/i18n";
import { planPriceLabel, planPeriodSuffix, planPriceWithPeriod } from "@/lib/pricing";

export const Route = createFileRoute("/settings/billing")({
  head: () => ({ meta: [{ title: "Plano — Viny Club" }] }),
  validateSearch: (s: Record<string, unknown>): { status?: string; session_id?: string } => ({
    status: typeof s.status === "string" ? s.status : undefined,
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
  }),
  component: SettingsBilling,
});

const freeFeatures = [
  "Coleção ilimitada de discos",
  "Wishlist ilimitada",
  "Marketplace — vender e trocar discos",
  "Chat direto com colecionadores",
  "Feed da comunidade",
];

const premiumFeatures: Array<{ text: string; isNew?: boolean }> = [
  { text: "Scroll infinito no Explorar" },
  { text: "Remover ads" },
  { text: "Criar grupos privados" },
  { text: "Insights da coleção" },
  { text: "Recursos beta antecipados" },
  { text: "Voto em novas funcionalidades" },
  { text: "Perfil Embaixador" },
  { text: "Badge de verificado" },
  { text: "Comunidade privada" },
  { text: "Prioridade no suporte" },
  { text: "Templates premium para mini site" },
];

function SettingsBilling() {
  const { isAuthenticated } = useCurrentUser();
  const { locale } = useLocale();
  const { status } = Route.useSearch();
  const navigate = Route.useNavigate();
  const get = useServerFn(getMySubscription);
  const portal = useServerFn(createPortalSession);
  const cancel = useServerFn(cancelPremium);
  const resume = useServerFn(resumePremium);
  const qc = useQueryClient();


  useEffect(() => {
    if (status === "success") {
      toast.success("Pagamento confirmado. Bem-vindo(a), Embaixador(a)!");
      qc.invalidateQueries({ queryKey: ["my-subscription"] });
      navigate({ to: "/settings/billing", search: {}, replace: true });
    }
  }, [status, navigate, qc]);

  const sub = useQuery({
    queryKey: ["my-subscription"],
    queryFn: () => get(),
    enabled: isAuthenticated,
  });

  const openPortal = useMutation({
    mutationFn: () =>
      portal({
        data: {
          returnUrl: `${window.location.origin}/settings/billing`,
          environment: getStripeEnvironment(),
        },
      }),
    onSuccess: (r) => {
      if ("error" in r) throw new Error(r.error);
      window.open(r.url, "_blank");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const stop = useMutation({
    mutationFn: () => cancel({ data: { environment: getStripeEnvironment() } }),
    onSuccess: () => {
      toast.success("Cancelamento agendado para o fim do período.");
      qc.invalidateQueries({ queryKey: ["my-subscription"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reactivate = useMutation({
    mutationFn: () => resume({ data: { environment: getStripeEnvironment() } }),
    onSuccess: () => {
      toast.success("Assinatura reativada.");
      qc.invalidateQueries({ queryKey: ["my-subscription"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!isAuthenticated) {
    return (
      <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-8 text-center">
        <p className="text-muted-foreground">Entre para gerenciar seu plano.</p>
        <Link
          to="/auth"
          className="mt-4 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
        >
          Entrar
        </Link>
      </div>
    );
  }

  const isPremium =
    sub.data?.plan === "premium" &&
    (sub.data.status === "active" || sub.data.status === "trialing");
  const interval = sub.data?.billing_interval ?? null;
  const cancelScheduled = sub.data?.cancel_at_period_end ?? false;
  const periodEnd = sub.data?.current_period_end
    ? new Date(sub.data.current_period_end).toLocaleDateString("pt-BR")
    : null;
  const returnUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/settings/billing?status=success&session_id={CHECKOUT_SESSION_ID}`;

  return (
    <div className="space-y-12">
      <PaymentTestModeBanner />

      <div className="reveal">
        <EyebrowBadge>§ Plano</EyebrowBadge>
        <h1 className="mt-3 font-display text-4xl font-medium tracking-[-0.03em] lg:text-5xl">
          Apoie o projeto
        </h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          Todas as funcionalidades já estão liberadas no plano grátis. Vire embaixador para apoiar
          o Viny, ganhar selo de verificado e acesso antecipado.
        </p>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-8">
        <div className="absolute inset-0 opacity-50 [background:radial-gradient(600px_circle_at_top_right,hsl(var(--primary)/0.08),transparent)]" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-4">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full ${
                isPremium ? "bg-primary/15 text-primary" : "bg-foreground/10 text-muted-foreground"
              }`}
            >
              <Crown className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Plano atual
              </div>
              <div className="mt-1 font-display text-2xl font-medium tracking-[-0.02em]">
                {isPremium ? "Embaixador(a)" : "Grátis"}
              </div>
              {isPremium && periodEnd && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {cancelScheduled ? "Encerra" : "Renova"} em {periodEnd}
                  {interval && ` · cobrança ${interval === "year" ? "anual" : "mensal"}`}
                </div>
              )}
            </div>
          </div>
          {isPremium && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => openPortal.mutate()}
                disabled={openPortal.isPending}
                className="inline-flex items-center gap-2 rounded-full border border-foreground/15 px-5 py-2.5 text-sm font-medium hover:bg-foreground/[0.04]"
              >
                {openPortal.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Gerenciar pagamento <ExternalLink className="h-3.5 w-3.5" />
              </button>
              {cancelScheduled ? (
                <button
                  onClick={() => reactivate.mutate()}
                  disabled={reactivate.isPending}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
                >
                  {reactivate.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Reativar
                </button>
              ) : (
                <button
                  onClick={() => stop.mutate()}
                  disabled={stop.isPending}
                  className="inline-flex items-center gap-2 rounded-full border border-foreground/15 px-5 py-2.5 text-sm font-medium hover:bg-foreground/[0.04]"
                >
                  {stop.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Cancelar assinatura
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-px overflow-hidden rounded-2xl border border-foreground/10 bg-foreground/10 md:grid-cols-2">
        <PlanCard
          eyebrow="№ 01 — Grátis"
          name="Grátis"
          price={locale === "en" ? "US$ 0" : "R$ 0"}
          priceSuffix="/ pra sempre"
          description="Todas as funcionalidades, sem limites. Faça parte da comunidade."
          features={freeFeatures}
          current={!isPremium}
          cta={
            <div className="mt-10 inline-flex w-full items-center justify-center rounded-full border border-foreground/15 bg-foreground/[0.03] px-6 py-3.5 text-sm font-medium text-muted-foreground">
              {!isPremium ? "Plano atual" : "Disponível ao cancelar"}
            </div>
          }
        />

        <PlanCard
          eyebrow="№ 02 — Embaixador(a)"
          name="Embaixador"
          price={planPriceLabel(locale)}
          priceSuffix={planPeriodSuffix(locale)}

          description="Apoie o Viny e desbloqueie perks exclusivos de embaixador."
          features={premiumFeatures}
          highlight
          current={isPremium}
          cta={
            isPremium ? (
              <button
                onClick={() => openPortal.mutate()}
                disabled={openPortal.isPending}
                className="mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full border border-foreground/15 px-6 py-3.5 text-sm font-semibold hover:bg-foreground/[0.04]"
              >
                {openPortal.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Gerenciar plano
              </button>
            ) : (
              <div className="mt-10 grid gap-2">
                <button
                  onClick={() => navigate({ to: "/assinar" })}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-xl shadow-primary/20 transition-transform hover:scale-[1.02]"
                >
                  Apoiar o projeto — {planPriceWithPeriod(locale)}
                </button>
              </div>
            )
          }
        />
      </div>

      <div className="space-y-2 text-center text-xs text-muted-foreground">
        <p>Pagamento processado com segurança pela Stripe. Cancele quando quiser.</p>
        <Link
          to="/settings/billing/diagnostics"
          className="inline-block underline underline-offset-4 hover:text-foreground"
        >
          Diagnóstico de billing →
        </Link>
      </div>

    </div>
  );
}

function PlanCard({
  eyebrow,
  name,
  price,
  priceSuffix,
  secondary,
  description,
  features,
  highlight,
  current,
  cta,
}: {
  eyebrow: string;
  name: string;
  price: string;
  priceSuffix: string;
  secondary?: string;
  description: string;
  features: Array<string | { text: string; isNew?: boolean }>;
  highlight?: boolean;
  current?: boolean;
  cta: React.ReactNode;
}) {
  return (
    <div className="relative flex flex-col bg-background p-8 lg:p-10">
      {highlight && (
        <div className="absolute right-6 top-6 rounded-full bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary-foreground">
          Embaixador
        </div>
      )}
      {current && !highlight && (
        <div className="absolute right-6 top-6 rounded-full bg-foreground/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Atual
        </div>
      )}
      <div
        className={`text-[10px] uppercase tracking-[0.3em] ${highlight ? "text-primary" : "text-muted-foreground"}`}
      >
        {eyebrow}
      </div>
      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-display text-6xl font-medium tracking-[-0.04em]">{price}</span>
        <span className="text-sm text-muted-foreground">{priceSuffix}</span>
      </div>
      {secondary && <div className="mt-1 text-xs text-muted-foreground">{secondary}</div>}
      <p className="mt-3 text-sm text-muted-foreground">{description}</p>
      <ul className="mt-8 space-y-3 text-sm">
        {features.map((f) => {
          const text = typeof f === "string" ? f : f.text;
          const isNew = typeof f === "object" && f.isNew;
          return (
            <li key={text} className="flex items-start gap-3">
              <Check
                className={`mt-[3px] h-4 w-4 shrink-0 ${highlight ? "text-primary" : "text-foreground/40"}`}
              />
              <span className="flex items-center gap-2">
                {text}
                {isNew && (
                  <Badge className="bg-primary/15 text-primary border-none text-[10px] px-1.5 py-0">
                    Novo
                  </Badge>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {cta}
      {/* unused name for prop completeness */}
      <span className="sr-only">{name}</span>
    </div>
  );
}
